/**
 * @file PickItemsTable.tsx
 * @description Таблица позиций задания на сборку для ≥lg (phase19.24 §3.1,
 * п. 0.4.2 фазы; переведена на общий `DocumentItemsTable` в этапе 1, шаг 1).
 *
 * До контракта десктоп был растянутой мобильной карточкой: одна и та же
 * колонка с фото на 1280px и на 360px. Раскладка «как в приёмке»: слева
 * таблица позиций, отметка — в правой панели, поэтому степперов в строке нет,
 * а действия по строке живут в «⋯».
 *
 * Колонки документа: «Откуда брать» и «Собрано / план». Отдельной колонки
 * «План» нет (решение пользователя 2026-09-20): план и так стоит в «Собрано»
 * вторым числом («2 / 3 шт»), а лишние 80px выталкивали таблицу за край
 * колонки 544px и включали горизонтальный скролл рамки на 1280px. Каркас
 * строки (фото, товар/артикул, «⋯») задаёт общий блок — он же у приёмки,
 * списания и перемещения.
 *
 * Строка с недостачей меняет состояние (§3.3): тон `danger`, а в «⋯» остаётся
 * единственный выход — «Снять отметку» (`PickItemRowMenu`).
 */

import { AlertTriangle, Check } from "lucide-react";

import { PickItemRowMenu } from "./PickItemRowMenu";
import { StockHintLine } from "./StockHintLine";
import {
  DocumentItemsTable,
  type DocumentRowTone,
  type DocumentTableColumn,
} from "../../ui/DocumentItemsTable";
import { WmsSkuThumb } from "../WmsSkuThumb";
import {
  PICKING_SHORTAGE_REASON_LABEL,
  formatPickItemName,
  isItemPicked,
  type PickingTaskItemDto,
} from "../../../lib/api-picking";
import { formatItemQty } from "../../../lib/picking-format";

interface Props {
  /** Строки после фильтра поиска; порядок задаёт сервер (маршрут обхода). */
  items: PickingTaskItemDto[];
  /** false — задание закрыто или чужое: показываем факт без действий. */
  editable: boolean;
  busy: boolean;
  /** Строка последнего скана — подсветка. */
  scannedItemId: string | null;
  onOpenManualQty: (itemId: string) => void;
  onOpenShortage: (itemId: string) => void;
  /** Снять отметку недостачи со строки (`shortageQty: 0` на сервере). */
  onClearShortage: (itemId: string) => void;
}

/** Тон строки: недостача важнее подсветки скана, собранная строка — гаснет. */
function rowTone(item: PickingTaskItemDto, scannedItemId: string | null): DocumentRowTone {
  if (Number(item.shortageQty) > 0) return "danger";
  if (scannedItemId === item.id) return "selected";
  return isItemPicked(item) ? "done" : "default";
}

export function PickItemsTable({
  items,
  editable,
  busy,
  scannedItemId,
  onOpenManualQty,
  onOpenShortage,
  onClearShortage,
}: Props) {
  const columns: DocumentTableColumn<PickingTaskItemDto>[] = [
    {
      key: "source",
      label: "Откуда брать",
      render: (item) => <StockHintLine item={item} />,
    },
    {
      key: "picked",
      label: "Собрано / план",
      // 132px хватает на «12 / 12 шт» с галочкой; остаток ширины забирает
      // «Откуда брать» — там адрес ячейки и партия.
      width: "132px",
      render: (item) => (
        <span className="flex items-center gap-1.5 tabular-nums">
          {isItemPicked(item) ? (
            <Check className="size-4 text-primary" aria-hidden />
          ) : null}
          {formatItemQty(item.qtyPicked, item.qtyPlanned, item.unit)}
        </span>
      ),
    },
  ];

  return (
    <DocumentItemsTable
      items={items}
      rowKey={(item) => item.id}
      rowId={(item) => `pick-item-${item.id}`}
      // 540px — каркас без горизонтального скролла: на 1280px левой колонке
      // достаётся 544px (сайдбар 240 + поля страницы + панель отметки 360).
      // Внутри: фото 80 + «Товар / артикул» 30% (≈162) + «Собрано / план» 132
      // + «⋯» 72, остаток ≈94px забирает «Откуда брать» — адрес ячейки при
      // необходимости переносится на вторую строку.
      minWidth="540px"
      columns={columns}
      media={(item) => <WmsSkuThumb url={item.sku.imageUrl} />}
      title={(item) => formatPickItemName(item)}
      code={(item) => item.skuCode}
      note={(item) =>
        Number(item.shortageQty) > 0 ? (
          <span className="flex items-start gap-1.5 text-xs text-destructive">
            <AlertTriangle className="mt-0.5 size-3.5 shrink-0" aria-hidden />
            <span>
              Недостача {formatItemQty(item.shortageQty, item.qtyPlanned, item.unit)}
              {item.shortageReason
                ? ` · ${PICKING_SHORTAGE_REASON_LABEL[item.shortageReason]}`
                : ""}
            </span>
          </span>
        ) : null
      }
      rowTone={(item) => rowTone(item, scannedItemId)}
      actions={(item) => (
        <PickItemRowMenu
          item={item}
          editable={editable}
          busy={busy}
          onOpenManualQty={() => onOpenManualQty(item.id)}
          onOpenShortage={() => onOpenShortage(item.id)}
          onClearShortage={() => onClearShortage(item.id)}
        />
      )}
    />
  );
}
